import { ComponentPropsWithoutRef, ReactNode, RefObject } from "react"

import { UseComboboxResult } from "./useCombobox"
import { useIntersectionObserver } from "../../../hooks/useIntersectionObserver"
import { classNames } from "../../../lib/classNames"
import { TextInputProps } from "../../base/input/text/TextInput"
import { PopoverTextInput } from "../../base/overlay/Popover"
import { popoverRowClassName } from "../../base/overlay/Popover/popoverStyles"

type ExtendedTextInputProps = TextInputProps & {
  ref?: RefObject<HTMLInputElement | null>
}

export interface ComboboxProps<T> {
  useComboboxRes: UseComboboxResult<T>
  renderItem: (item: T, isHighlighted: boolean) => ReactNode
  renderEmptyState: () => ReactNode
  keyExtractor: (item: T) => string
  comboboxContainerProps?: ComponentPropsWithoutRef<"div">
  comboboxInputContainerProps?: ComponentPropsWithoutRef<"div">
  comboboxListProps?: ComponentPropsWithoutRef<"ul">
  comboboxListItemProps?: ComponentPropsWithoutRef<"li">
  comboboxInputProps?: ExtendedTextInputProps
  onLoadMore?: () => void
}

export function Combobox<T>({
  renderItem,
  renderEmptyState,
  keyExtractor,
  comboboxContainerProps,
  comboboxInputContainerProps,
  comboboxListProps,
  comboboxListItemProps,
  comboboxInputProps,
  useComboboxRes: useComboboxReturnValue,
  onLoadMore,
}: ComboboxProps<T>) {
  const {
    getInputProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    inputValue,
    isOpen,
    filteredItems,
    inline,
  } = useComboboxReturnValue

  const { label: _label, ...inputProps } = comboboxInputProps ?? {}

  const lastItemRef = useIntersectionObserver<HTMLLIElement>(() => {
    onLoadMore?.()
  })

  return (
    <div className="w-80" {...comboboxContainerProps}>
      <div className="p-2" {...comboboxInputContainerProps}>
        <PopoverTextInput {...getInputProps(inputProps)} />
      </div>
      <ul
        {...getMenuProps(comboboxListProps)}
        className={classNames(
          "max-h-100 space-y-1 overflow-y-auto p-1",
          comboboxListProps?.className
        )}
      >
        {(isOpen || inline) &&
          (inputValue && filteredItems.length === 0 ? (
            <li {...comboboxListItemProps}>{renderEmptyState()}</li>
          ) : (
            filteredItems.map((item, index) => {
              const isLast = index === filteredItems.length - 1
              return (
                <li
                  key={keyExtractor(item)}
                  {...getItemProps({
                    item,
                    index,
                    ref: isLast && onLoadMore ? lastItemRef : undefined,
                    ...comboboxListItemProps,
                  })}
                >
                  {renderItem(item, isOpen && highlightedIndex === index)}
                </li>
              )
            })
          ))}
      </ul>
    </div>
  )
}

export function DefaultEmptyState() {
  return <div className={popoverRowClassName}>No results found</div>
}

interface DefaultRowProps<T> {
  item: T
  isHighlighted: boolean
  accessoryExtractor?: (item: T) => ReactNode
  valueExtractor: (item: T) => string
}
export function DefaultRow<T>({
  item,
  isHighlighted,
  valueExtractor,
  accessoryExtractor,
}: DefaultRowProps<T>) {
  const accessory = accessoryExtractor?.(item)

  return (
    <div
      className={highlightedRowClassName(
        isHighlighted,
        accessory ? "flex items-center gap-x-2" : undefined
      )}
    >
      {accessory}
      {valueExtractor(item)}
    </div>
  )
}

export function highlightedRowClassName(
  isHighlighted: boolean,
  rowClassName?: string
) {
  return classNames(
    popoverRowClassName,
    rowClassName,
    isHighlighted && "bg-v2-background-surface-strong"
  )
}
